"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Calendar as CalendarIcon, Upload, Check, ChevronRight, ChevronLeft, Image as ImageIcon, User, Loader, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
interface FileWithPreview extends File {
  preview: string;
}
export default function StartYourProject() {
  return;
}
function MultiStepForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage
  } = useWvcForm();
  const {
    trigger,
    setValue,
    watch
  } = useFormContext();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const watchedDate = watch("preferred_date");
  useEffect(() => {
    if (watchedDate) {
      setDate(new Date(watchedDate));
    }
  }, [watchedDate]);
  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    if (newDate) {
      const offsetDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);
      const formatted = offsetDate.toISOString().split('T')[0];
      setValue("preferred_date", formatted, {
        shouldValidate: true
      });
    } else {
      setValue("preferred_date", "", {
        shouldValidate: true
      });
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      }));
      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);
      setValue("photos", updatedFiles);
    }
  };
  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setValue("photos", newFiles);
  };
  const nextStep = async () => {
    let isValid = false;
    if (step === 1) {
      isValid = await trigger("preferred_date");
    } else if (step === 2) {
      isValid = true;
    }
    if (isValid) {
      setStep(prev => prev + 1);
      const formElement = document.getElementById('start-your-project');
      if (formElement && window.innerWidth < 768) {
        formElement.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  };
  const prevStep = () => {
    setStep(prev => prev - 1);
  };
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Request Sent", {
        description: successMessage,
        duration: 5000
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  const progress = step / 3 * 100;
  if (isSubmitted && !submitError) {
    return <div className="p-12 flex flex-col items-center justify-center text-center min-h-[400px] animate-in fade-in duration-500">
        <div className="w-24 h-24 bg-white/10 text-white rounded-full flex items-center justify-center mb-8 border border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
          <Check className="w-10 h-10" />
        </div>
        <h3 className="text-3xl font-bold text-white mb-4">Request Received</h3>
        <p className="text-muted-foreground max-w-md mb-8 text-lg">
          {successMessage}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()} className="border-white/20 hover:bg-white/10 text-white">
          Start Another Project
        </Button>
      </div>;
  }
  return <div className="flex flex-col min-h-[600px]">
      {/* Progress Header */}
      <div className="border-b border-white/10 bg-black/20 p-6 md:p-8">
        <div className="flex justify-between text-sm font-medium text-muted-foreground mb-6">
          <div className={cn("flex items-center gap-2 transition-colors", step >= 1 ? "text-white" : "opacity-50")}>
            <span className="flex items-center justify-center w-6 h-6 rounded-full border border-current text-xs">1</span>
            <span className="hidden md:inline">Date</span>
          </div>
          <div className={cn("flex items-center gap-2 transition-colors", step >= 2 ? "text-white" : "opacity-50")}>
            <span className="flex items-center justify-center w-6 h-6 rounded-full border border-current text-xs">2</span>
            <span className="hidden md:inline">Media</span>
          </div>
          <div className={cn("flex items-center gap-2 transition-colors", step >= 3 ? "text-white" : "opacity-50")}>
            <span className="flex items-center justify-center w-6 h-6 rounded-full border border-current text-xs">3</span>
            <span className="hidden md:inline">Details</span>
          </div>
        </div>
        <Progress value={progress} className="h-1 bg-white/10" />
      </div>

      {/* Form Content */}
      <Form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <div className="flex-1 p-6 md:p-10">
          
          {/* Step 1: Date Selection */}
          {step === 1 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">Select Preferred Date</h3>
                <p className="text-muted-foreground">When would you like to start your project?</p>
              </div>
              
              <div className="flex justify-center">
                <FormField control={control} name="preferred_date" render={({
              field
            }) => <FormItem className="flex flex-col items-center w-full max-w-sm">
                      <FormControl>
                        <div className="p-6 bg-black/40 border border-white/10 rounded-xl shadow-inner backdrop-blur-md w-full flex justify-center">
                          <SafeHydrate><Calendar mode="single" selected={date} onSelect={handleDateSelect} className="rounded-md border-none text-white w-full" classNames={{
                      head_cell: "text-muted-foreground font-normal",
                      cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-white/10 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                      day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-white/10 rounded-md transition-colors text-white",
                      day_selected: "bg-white text-black hover:bg-white hover:text-black focus:bg-white focus:text-black font-bold",
                      day_today: "bg-white/10 text-white border border-white/20",
                      day_outside: "text-muted-foreground opacity-50",
                      day_disabled: "text-muted-foreground opacity-50",
                      nav_button: "border border-white/10 hover:bg-white/10 hover:text-white",
                      caption: "text-white font-medium mb-4"
                    }} disabled={date => date < new Date()} /></SafeHydrate>
                        </div>
                      </FormControl>
                      <FormMessage />
                      <input type="hidden" {...field} value={field.value || ''} />
                    </FormItem>} />
              </div>
            </div>}

          {/* Step 2: Media Upload */}
          {step === 2 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">Upload Media</h3>
                <p className="text-muted-foreground">Share photos or videos of your space (Optional)</p>
              </div>

              <FormField control={control} name="photos" render={() => <FormItem>
                    <FormControl>
                      <div>
                        <div className="group border-2 border-dashed border-white/10 hover:border-white/30 hover:bg-white/5 transition-all duration-300 rounded-2xl p-12 text-center cursor-pointer relative overflow-hidden" onClick={() => fileInputRef.current?.click()}>
                          <input type="file" className="hidden" ref={fileInputRef} onChange={handleFileChange} multiple accept="image/*,video/*" />
                          <div className="flex flex-col items-center gap-4 relative z-10">
                            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center group-hover:bg-white/10 transition-colors duration-300 border border-white/10">
                              <Upload className="w-8 h-8 text-white/60 group-hover:text-white transition-colors duration-300" />
                            </div>
                            <div className="space-y-2">
                              <p className="text-white font-medium text-lg">Click to upload files</p>
                              <p className="text-muted-foreground text-sm">or drag and drop</p>
                              <p className="text-muted-foreground text-xs">Images or Videos (max 10MB each)</p>
                            </div>
                          </div>
                        </div>

                        {files.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-6">
                            {files.map((file, i) => <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-white/10 bg-black/40">
                                <img src={file.preview} alt="preview" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                  <Button type="button" variant="destructive" size="icon" onClick={() => removeFile(i)} className="rounded-full w-10 h-10">
                                    <X className="w-5 h-5" />
                                  </Button>
                                </div>
                              </div>)}
                          </div>}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>}

          {/* Step 3: Final Details */}
          {step === 3 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500 max-w-3xl mx-auto">
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">Final Details</h3>
                <p className="text-muted-foreground">Tell us about yourself and the project specifics.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-white/80">Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-white/80">Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-white/80">Phone Number</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="square_footage" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-white/80">Square Footage</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type="number" placeholder="e.g. 500" {...field} className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 h-12 pr-12" />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">ft²</span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="floor_type" render={({
              field
            }) => <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel className="text-white/80 mb-3 block">Floor Type Interest</FormLabel>
                      <FormControl>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {["Flake", "Metallic", "Other"].map(type => <label key={type} className={cn("cursor-pointer border rounded-xl p-4 text-center transition-all duration-200 relative overflow-hidden group", field.value === type ? "border-white bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.2)]" : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white")}>
                              <input type="radio" className="hidden" value={type} checked={field.value === type} onChange={() => field.onChange(type)} />
                              <span className="font-medium relative z-10">{type}</span>
                              {field.value === type && <div className="absolute inset-0 bg-gradient-to-tr from-white via-gray-100 to-white opacity-100" />}
                            </label>)}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>

              {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3 text-red-400 text-sm">
                  <Info className="w-5 h-5 shrink-0 mt-0.5" />
                  <p>{submitError}</p>
                </div>}
            </div>}
        </div>

        {/* Navigation Footer */}
        <div className="p-6 md:p-8 border-t border-white/10 bg-black/20 flex justify-between items-center">
          <Button type="button" variant="ghost" onClick={prevStep} disabled={step === 1 || isSubmitting} className={cn("text-muted-foreground hover:text-white hover:bg-white/5 transition-all", step === 1 && "invisible")}>
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {step < 3 ? <Button type="button" onClick={nextStep} className="bg-white text-black hover:bg-white/90 min-w-[140px] h-12 rounded-lg font-medium text-base shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.3)] transition-all">
              Next Step <ChevronRight className="w-4 h-4 ml-2" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-white text-black hover:bg-white/90 min-w-[160px] h-12 rounded-lg font-bold text-base shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all">
              {isSubmitting ? <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" /> Processing
                </> : "Submit Project"}
            </Button>}
        </div>
      </Form>
    </div>;
}